import * as React from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bookmark,
  BookmarkPlus,
  ChevronDown,
  Pencil,
  Settings,
  X,
  XCircle,
} from 'lucide-react'
import type { ColumnFiltersState } from '@tanstack/react-table'
import type { ColumnDefinition } from '../../Table/types'
import {
  listSavedViews,
  savedViewsQueryKey,
  type SavedView,
  type SavedViewConfig,
} from '../../../../app/lib/api/saved-views'
import {
  loadIncludedSharedViewIds,
  loadSelectedSavedViewId,
  saveIncludedSharedViewIds,
  saveSelectedSavedViewId,
} from '../../Table/tablePreferences'
import { getPortalContainer } from '../../../lib/portalContainer'
import { useT } from '../../../../app/lib/i18n'
import { Modal } from '../../Modal'
import { Button } from '../../Button'
import {
  applySnapshotToPrefs,
  configFromSnapshot,
  findMatchingView,
  snapshotFromPrefs,
  type SavedViewSnapshot,
} from './snapshot'
import { getSavedViewIcon } from './icons'
import { SavedViewFormModal } from './SavedViewFormModal'
import { SavedViewSettingsModal } from './SavedViewSettingsModal'
import * as s from './SavedViews.css'

/** Temporary UI stubs — remove once real org-shared views exist in the API. */
const STUB_SHARED_VIEWS_USERS: SavedView[] = [
  {
    id: 'stub-shared-users-unverified',
    ownerUserId: 'stub-owner-1',
    ownerName: 'Alex Rivera',
    resourceType: 'users',
    listId: 'admin-users',
    name: 'Unverified members',
    icon: 'mail',
    color: '#EF4444',
    visibility: 'org',
    config: {
      columnFilters: [{ id: 'status', value: ['unverified'] }],
      search: '',
      sort: { sort_by: 'name', order: 'asc' },
      visibleFilterIds: ['status', 'organizations', 'phoneVerified'],
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'stub-shared-users-phone',
    ownerUserId: 'stub-owner-2',
    ownerName: 'Jordan Lee',
    resourceType: 'users',
    listId: 'admin-users',
    name: 'Phone verified only',
    icon: 'users',
    color: '#3B82F6',
    visibility: 'org',
    config: {
      columnFilters: [{ id: 'phoneVerified', value: ['yes'] }],
      search: '',
      sort: { sort_by: 'name', order: 'asc' },
      visibleFilterIds: ['status', 'organizations', 'phoneVerified'],
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'stub-shared-users-search',
    ownerUserId: 'stub-owner-3',
    ownerName: 'Sam Patel',
    resourceType: 'users',
    listId: 'admin-users',
    name: 'Name contains “tech”',
    icon: 'filter',
    color: '#10B981',
    visibility: 'org',
    config: {
      columnFilters: [],
      search: 'tech',
      sort: { sort_by: 'name', order: 'asc' },
      visibleFilterIds: ['status', 'organizations', 'phoneVerified'],
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

export interface SavedViewsControlProps<TRow> {
  listId: string
  resourceType: string
  userId?: string
  accessToken: string
  columns: ColumnDefinition<TRow>[]
  columnFilters: ColumnFiltersState
  search: string
  sort: { sort_by: string; order: 'asc' | 'desc' } | null | undefined
  visibleFilterIds: string[]
  onApplySnapshot: (snapshot: SavedViewSnapshot) => void
  onClearFilters: () => void
  /** Called when a saved view is applied or cleared — for org-context precedence. */
  onActiveViewChange?: (viewId: string | null) => void
  /** Open the views list in a modal instead of a dropdown (narrow chrome). */
  presentation?: 'menu' | 'modal'
  /** Stretch the trigger to fill available width (narrow chrome row). */
  fillWidth?: boolean
}

export function SavedViewsControl<TRow>({
  listId,
  resourceType,
  userId,
  accessToken,
  columns,
  columnFilters,
  search,
  sort,
  visibleFilterIds,
  onApplySnapshot,
  onClearFilters,
  onActiveViewChange,
  presentation = 'menu',
  fillWidth = false,
}: SavedViewsControlProps<TRow>) {
  const t = useT()
  const queryClient = useQueryClient()
  const [menuOpen, setMenuOpen] = React.useState(false)
  const useModal = presentation === 'modal'
  const [formOpen, setFormOpen] = React.useState(false)
  const [settingsOpen, setSettingsOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<SavedView | null>(null)
  const [includedSharedIds, setIncludedSharedIds] = React.useState<string[]>(() =>
    userId ? loadIncludedSharedViewIds(userId, resourceType) : [],
  )
  const [selectedId, setSelectedId] = React.useState<string | null>(() =>
    userId ? loadSelectedSavedViewId(userId, listId) : null,
  )

  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number } | null>(null)

  const { data } = useQuery({
    queryKey: savedViewsQueryKey(resourceType),
    queryFn: () => listSavedViews(accessToken, resourceType),
    enabled: Boolean(accessToken && resourceType),
    staleTime: 30_000,
  })

  const personal = data?.personal ?? []
  const sharedAvailable = React.useMemo(() => {
    const fromApi = data?.sharedAvailable ?? []
    if (resourceType !== 'users') return fromApi
    const ids = new Set(fromApi.map((v) => v.id))
    const stubs = STUB_SHARED_VIEWS_USERS.filter((v) => !ids.has(v.id))
    return [...fromApi, ...stubs]
  }, [data?.sharedAvailable, resourceType])

  // Drop stale opt-ins that no longer exist in sharedAvailable
  React.useEffect(() => {
    if (!data || !userId) return
    const availableIds = new Set(sharedAvailable.map((v) => v.id))
    const next = includedSharedIds.filter((id) => availableIds.has(id))
    if (next.length !== includedSharedIds.length) {
      setIncludedSharedIds(next)
      saveIncludedSharedViewIds(userId, resourceType, next)
    }
  }, [data, sharedAvailable, includedSharedIds, userId, resourceType])

  const optedInShared = React.useMemo(
    () => sharedAvailable.filter((v) => includedSharedIds.includes(v.id)),
    [sharedAvailable, includedSharedIds],
  )

  const matchableViews = React.useMemo(
    () => [...personal, ...optedInShared],
    [personal, optedInShared],
  )

  const currentSnapshot = React.useMemo(
    () =>
      snapshotFromPrefs({
        columnFilters,
        search,
        sort: sort ?? null,
        visibleFilterIds,
      }),
    [columnFilters, search, sort, visibleFilterIds],
  )

  const currentConfig: SavedViewConfig = React.useMemo(
    () => configFromSnapshot(currentSnapshot),
    [currentSnapshot],
  )

  const hasActiveFilters =
    columnFilters.length > 0 || (search ?? '').trim().length > 0

  const matched = React.useMemo(
    () => findMatchingView(matchableViews, currentSnapshot),
    [matchableViews, currentSnapshot],
  )

  // Sync selected id from match (clear when filters diverge)
  React.useEffect(() => {
    const nextId = matched?.id ?? null
    if (nextId === selectedId) return
    setSelectedId(nextId)
    if (userId) saveSelectedSavedViewId(userId, listId, nextId)
    onActiveViewChange?.(nextId)
  }, [matched?.id, selectedId, userId, listId, onActiveViewChange])

  const selectedView =
    matchableViews.find((v) => v.id === selectedId) ?? matched ?? null

  const hasAnyViews = personal.length > 0 || optedInShared.length > 0
  const saveDisabled = Boolean(matched) || !hasActiveFilters

  const persistIncluded = (ids: string[]) => {
    setIncludedSharedIds(ids)
    if (userId) saveIncludedSharedViewIds(userId, resourceType, ids)
  }

  const openMenu = () => {
    if (useModal) {
      setMenuOpen(true)
      return
    }
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const menuWidth = Math.min(300, window.innerWidth * 0.9)
    const left = Math.max(8, rect.right - menuWidth)
    setMenuPos({ top: rect.bottom + 4, left })
    setMenuOpen(true)
  }

  React.useEffect(() => {
    if (!menuOpen || useModal) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return
      setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen, useModal])

  const applyView = (view: SavedView) => {
    const snapshot = applySnapshotToPrefs(view.config, columns)
    onApplySnapshot(snapshot)
    setSelectedId(view.id)
    if (userId) saveSelectedSavedViewId(userId, listId, view.id)
    onActiveViewChange?.(view.id)
    setMenuOpen(false)
  }

  const clearActive = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onClearFilters()
    setSelectedId(null)
    if (userId) saveSelectedSavedViewId(userId, listId, null)
    onActiveViewChange?.(null)
  }

  const openCreate = () => {
    if (saveDisabled) return
    setEditing(null)
    setFormOpen(true)
    setMenuOpen(false)
  }

  const openEdit = (view: SavedView, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditing(view)
    setFormOpen(true)
    setMenuOpen(false)
  }

  const removeShared = (view: SavedView, e: React.MouseEvent) => {
    e.stopPropagation()
    const next = includedSharedIds.filter((id) => id !== view.id)
    persistIncluded(next)
    if (selectedId === view.id) {
      setSelectedId(null)
      if (userId) saveSelectedSavedViewId(userId, listId, null)
      onActiveViewChange?.(null)
    }
  }

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: savedViewsQueryKey(resourceType) })
  }

  const renderOption = (view: SavedView, opts: { owned: boolean }) => {
    const Icon = getSavedViewIcon(view.icon)
    const isSelected = selectedView?.id === view.id
    return (
      <div
        key={view.id}
        className={s.optionRow}
        data-selected={isSelected ? 'true' : undefined}
        role="option"
        aria-selected={isSelected}
        onClick={() => {
          if (!isSelected) applyView(view)
        }}
      >
        <span
          className={s.optionIcon}
          style={{ backgroundColor: `${view.color}22`, color: view.color }}
        >
          <Icon size={14} />
        </span>
        <span className={s.optionName}>{view.name}</span>
        {opts.owned ? (
          <button
            type="button"
            className={s.optionAction}
            title={t('shared.common.edit')}
            aria-label={`Edit ${view.name}`}
            onClick={(e) => openEdit(view, e)}
          >
            <Pencil size={14} />
          </button>
        ) : (
          <button
            type="button"
            className={s.optionAction}
            title={t('shared.savedView.removeFromMyList')}
            aria-label={`Remove ${view.name}`}
            onClick={(e) => removeShared(view, e)}
          >
            <XCircle size={14} />
          </button>
        )}
      </div>
    )
  }

  const saveThisViewRow = (
    <button
      type="button"
      className={s.optionRow}
      data-disabled={saveDisabled ? 'true' : undefined}
      disabled={saveDisabled}
      onClick={openCreate}
      title={
        !hasActiveFilters
          ? 'Apply filters before saving a view'
          : matched
            ? 'Current filters already match a saved view'
            : undefined
      }
    >
      <span className={`${s.optionIcon} ${s.optionIconNeutral}`}>
        <BookmarkPlus size={14} />
      </span>
      <span className={s.optionName}>{t('shared.savedView.saveThisView')}</span>
      <span className={s.optionActionSpacer} aria-hidden />
    </button>
  )

  const openSettings = () => {
    setSettingsOpen(true)
    setMenuOpen(false)
  }

  const menuList = (
    <>
      <div className={s.menuHeader}>{saveThisViewRow}</div>
      <div className={s.menuScroll}>
        {personal.length > 0 ? (
          <>
            <div className={s.sectionLabel}>{t('shared.savedView.personal')}</div>
            {personal.map((v) => renderOption(v, { owned: true }))}
          </>
        ) : null}
        {optedInShared.length > 0 ? (
          <>
            <div className={s.sectionLabel}>{t('shared.savedView.shared')}</div>
            {optedInShared.map((v) => renderOption(v, { owned: false }))}
          </>
        ) : null}
      </div>
    </>
  )

  const menu =
    useModal && menuOpen ? (
      <Modal
        open={menuOpen}
        setOpen={setMenuOpen}
        minWidth="360px"
        maxWidth="420px"
        minHeight="auto"
        maxHeight="85vh"
        container={getPortalContainer()}
      >
        <Modal.Header>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{t('shared.savedView.savedViews')}</h3>
        </Modal.Header>
        <Modal.Body>
          <div className={s.menuModalBody}>{menuList}</div>
        </Modal.Body>
        <Modal.Footer>
          <div className={s.menuModalFooterStack}>
            <Button
              type="button"
              color="neutral"
              variant="outline"
              fullWidth
              iconLeft={<Settings size={16} />}
              onClick={openSettings}
            >
              Settings
            </Button>
            <Button
              type="button"
              color="primary"
              variant="solid"
              fullWidth
              onClick={() => setMenuOpen(false)}
            >
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    ) : !useModal && menuOpen && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            className={s.menu}
            style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
            role="listbox"
          >
            {menuList}
            <div className={s.menuFooter}>
              <button
                type="button"
                className={s.gearBtn}
                title={t('shared.savedView.settings')}
                aria-label={t('shared.savedView.settings')}
                onClick={openSettings}
              >
                <Settings size={16} />
              </button>
            </div>
          </div>,
          getPortalContainer(),
        )
      : null

  const formAndSettings = (
    <>
      {formOpen ? (
        <SavedViewFormModal
          open={formOpen}
          onOpenChange={setFormOpen}
          accessToken={accessToken}
          resourceType={resourceType}
          listId={listId}
          currentConfig={currentConfig}
          existing={editing}
          onSaved={(view) => {
            invalidate()
            applyView(view)
          }}
          onDeleted={(id) => {
            invalidate()
            if (selectedId === id) clearActive()
          }}
        />
      ) : null}
      <SavedViewSettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        sharedAvailable={sharedAvailable}
        includedSharedIds={includedSharedIds}
        onChangeIncluded={persistIncluded}
      />
    </>
  )

  if (!hasAnyViews) {
    return (
      <div className={s.rowAboveToolbar} data-fill={fillWidth ? 'true' : undefined}>
        <div className={s.saveViewBtnGroup}>
          <button
            type="button"
            className={s.saveViewBtn}
            onClick={openCreate}
            disabled={saveDisabled}
            title={
              !hasActiveFilters
                ? 'Apply filters before saving a view'
                : undefined
            }
          >
            <BookmarkPlus size={14} />
            Save this view
          </button>
          <button
            type="button"
            className={s.gearBtn}
            title={t('shared.savedView.settings')}
            aria-label={t('shared.savedView.settings')}
            onClick={() => setSettingsOpen(true)}
          >
            <Settings size={16} />
          </button>
        </div>
        {formAndSettings}
      </div>
    )
  }

  const SelectedIcon = selectedView
    ? getSavedViewIcon(selectedView.icon)
    : null

  return (
    <div className={s.rowAboveToolbar} data-fill={fillWidth ? 'true' : undefined}>
      <button
        ref={triggerRef}
        type="button"
        className={s.trigger}
        onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
        aria-expanded={menuOpen}
        aria-haspopup={useModal ? 'dialog' : 'listbox'}
      >
        <Bookmark className={s.triggerIcon} size={14} aria-hidden />
        {SelectedIcon && selectedView ? (
          <span
            className={s.optionIcon}
            style={{
              backgroundColor: `${selectedView.color}22`,
              color: selectedView.color,
            }}
            aria-hidden
          >
            <SelectedIcon size={14} />
          </span>
        ) : null}
        <span
          className={s.triggerLabel}
          data-empty={selectedView ? undefined : 'true'}
        >
          {selectedView?.name ?? 'Saved views'}
        </span>
        {selectedView ? (
          <span
            className={s.clearBtn}
            role="button"
            tabIndex={0}
            title={t('shared.savedView.clearSavedView')}
            aria-label={t('shared.savedView.clearSavedView')}
            onClick={clearActive}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                clearActive()
              }
            }}
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
        )}
      </button>
      {menu}
      {formAndSettings}
    </div>
  )
}
